/**
 * Inline card-payment form for the WooPay (Stripe Express via WooPayments)
 * flow. Mirrors the Mind-Hack-v2 working pattern:
 *
 *   1. loadStripe(publishableKey, { stripeAccount: acct_… })
 *   2. CardElement collects the card client-side.
 *   3. stripe.createPaymentMethod → pm_…
 *   4. POST /api/pay/wcpay/start { orderId, paymentMethodId, donor } →
 *        { clientSecret?, ajaxNonce?, intentId?, intentType?, paymentStatus? }
 *   5. If a clientSecret came back → stripe.handleNextAction(...)
 *   6. POST /api/pay/wcpay/finalize { orderId, intentId, ajaxNonce } → ledger
 *
 * Renders inline (no modal). Card form sits in the same column as the
 * surrounding billing/amount form.
 */
import React from "react";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { Stripe, StripeCardElement, StripeElementsOptions } from "@stripe/stripe-js";
import { getStripe } from "@/services/stripe";
import {
  finalizeCardPayment,
  startCardPayment,
  type DonorPayload,
  type Recognition,
  type StartPaymentResponse,
} from "@/services/give-api";

type State = "ready" | "submitting" | "confirming" | "succeeded" | "error";

interface OuterProps {
  orderId: number;
  donor: DonorPayload;
  recognition: Recognition;
  amountUsd: number;
  /** Called once finalize confirms the order is paid. */
  onSucceeded: (intentId: string) => void;
}

export function EmbeddedPaymentForm(props: OuterProps) {
  const [stripePromise, setStripePromise] = React.useState<Promise<Stripe | null> | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sp = await getStripe();
        if (!cancelled) setStripePromise(Promise.resolve(sp));
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="border border-state-pending bg-paper-2 p-6 font-mono text-xs text-ink-2 leading-relaxed">
        <strong className="text-ink">Card payment unavailable.</strong>
        <br />
        {error}
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="border border-hairline bg-paper p-6 font-mono text-xs text-ink-3">
        Preparing secure payment…
      </div>
    );
  }

  // No clientSecret to pass yet — Stripe Elements is initialised in
  // "deferred intent" mode; we only need it to host the CardElement and run
  // createPaymentMethod / handleNextAction.
  const options: StripeElementsOptions = {
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: "#D97757",
        colorText: "#111111",
        colorTextSecondary: "#6e6e6e",
        colorBackground: "#fcfcfb",
        fontFamily: "ui-sans-serif, system-ui, sans-serif",
        borderRadius: "0px",
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <InnerForm {...props} />
    </Elements>
  );
}

function InnerForm({ orderId, donor, amountUsd, onSucceeded }: OuterProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [state, setState] = React.useState<State>("ready");
  const [error, setError] = React.useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (state === "submitting" || state === "confirming") return;

    setError(null);
    setState("submitting");

    const cardEl = elements.getElement(CardElement);
    if (!cardEl) {
      setError("Card field did not load — please reload and try again.");
      setState("error");
      return;
    }

    try {
      // 1. Tokenise the card → pm_…
      const pmResult = await stripe.createPaymentMethod({
        type: "card",
        card: cardEl as unknown as StripeCardElement,
        billing_details: {
          name: `${donor.firstName} ${donor.lastName}`.trim(),
          email: donor.email,
          phone: donor.phone || undefined,
          address: donor.address
            ? {
                line1: donor.address.line1 || undefined,
                line2: donor.address.line2 || undefined,
                city: donor.address.city || undefined,
                state: donor.address.state || undefined,
                postal_code: donor.address.postcode || undefined,
                country: donor.address.country || "US",
              }
            : undefined,
        },
      });
      if (pmResult.error) throw new Error(pmResult.error.message ?? "Card was rejected.");
      const paymentMethod = pmResult.paymentMethod;
      if (!paymentMethod?.id) throw new Error("Could not create payment method.");

      // 2. Server: WC Store API checkout → returns next-action info.
      const start: StartPaymentResponse = await startCardPayment({
        orderId,
        paymentMethodId: paymentMethod.id,
        donor,
      });

      // 3. WC may have settled the order without 3DS — short-circuit.
      if (start.paymentStatus === "success" && !start.clientSecret) {
        setState("succeeded");
        // Server-side fan-out (donor upsert + receipt) runs from the
        // finalize handler. When WC short-circuits we still call finalize
        // with the intent id WC surfaced, falling back to the order id.
        const fallbackIntentId = String(start.intentId ?? `wc_${orderId}`);
        if (start.ajaxNonce && start.intentId) {
          await finalizeCardPayment({
            orderId,
            intentId: String(start.intentId),
            ajaxNonce: start.ajaxNonce,
          }).catch(() => undefined);
        }
        onSucceeded(fallbackIntentId);
        return;
      }

      if (!start.clientSecret || !start.ajaxNonce) {
        throw new Error(
          start.message ??
            "WooPayments did not return a clientSecret. Please try again or contact the Foundation.",
        );
      }

      setState("confirming");

      // 4. Drive 3DS / SCA via Stripe.js using the WooPay-issued clientSecret.
      const next = await stripe.handleNextAction({ clientSecret: start.clientSecret });
      if (next.error) throw new Error(next.error.message ?? "Card authentication failed.");
      const intentId =
        next.paymentIntent?.id ?? next.setupIntent?.id ?? start.intentId ?? "";
      if (!intentId) throw new Error("Bank did not return a payment intent id after authentication.");

      // 5. Server: complete the order with admin-ajax.
      const fin = await finalizeCardPayment({
        orderId,
        intentId,
        ajaxNonce: start.ajaxNonce,
      });
      if (!fin.orderPaid) {
        throw new Error(`Donation finalize returned status '${fin.status}' — please contact the Foundation.`);
      }

      setState("succeeded");
      onSucceeded(intentId);
    } catch (err) {
      setState("error");
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const disabled = !stripe || !elements || state === "submitting" || state === "confirming" || state === "succeeded";
  const label =
    state === "submitting" || state === "confirming"
      ? "Authorising payment…"
      : state === "succeeded"
        ? "Thank you"
        : `Pay US$${amountUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="border border-hairline bg-paper p-4">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "15px",
                color: "#111111",
                fontFamily: "ui-sans-serif, system-ui, sans-serif",
                "::placeholder": { color: "#6e6e6e" },
              },
              invalid: { color: "#b8463b" },
            },
            hidePostalCode: true,
          }}
        />
      </div>
      {error && (
        <p className="font-mono text-xs text-state-pending leading-relaxed">{error}</p>
      )}
      <button
        type="submit"
        disabled={disabled}
        className="w-full bg-ink text-paper px-6 py-4 font-mono text-xs uppercase tracking-widest hover:bg-ink-2 disabled:opacity-60"
      >
        {label}
      </button>
      <p className="font-mono text-[10px] text-ink-3 text-center leading-relaxed">
        Card details are tokenised by Stripe directly. The Foundation never sees, stores, or transmits your card number.
      </p>
    </form>
  );
}
